import * as preact from "preact";

import * as premiumUpsellModal from "premiumUpsellModal";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as signupModal from "signupModal";
import stores from "stores/stores";
import Button from "ui/Button";
import SkeletonView from "views/SkeletonView";

type Props = {
  appRouter: any;
};

type State =
  | { type: "not-available" }
  | { type: "loading" }
  | {
      type: "loaded";
      userChats?: {
        _id: string;
        summary: string;
        title: string;
        timestamp: number;
      }[];
      recentNotificationMessage?: {
        _id: string;
        message: string;
      };
    };

class ChatListPageView extends preact.Component<Props, State> {
  private disposeLanguageWatch?: () => void;

  constructor(props: Props) {
    super(props);

    this.state = { type: "loading" };

    if (
      !languageMap.languageSupportsChat(
        stores.user.currentLearningLanguage.value
      )
    ) {
      this.props.appRouter.navigate("/learn", { trigger: true });
    }

    if (!stores.user.hasChatFeature.value) {
      this.state = { type: "not-available" };
      // User doesn't have access to the chat feature
      return;
    }

    // This is reduntant while we have the above check, keeping for possible future use
    this.checkSignIn();

    this.fetchChatHistory();

    this.disposeLanguageWatch = stores.user.onChange(
      (a) => a.currentLearningLanguage,
      this.onLanguageChange
    );
  }

  componentWillUnmount() {
    this.disposeLanguageWatch?.();
  }

  onLanguageChange = async () => {
    this.setState({ type: "loading" });
    await this.fetchChatHistory();
  };

  async fetchChatHistory() {
    const { userChats, recentNotificationMessage } = (
      await readlangClient.request(
        "GET",
        `/api/chat/history/${stores.user.currentLearningLanguage.value}`,
        {}
      )
    ).data;

    this.setState({ type: "loaded", userChats, recentNotificationMessage });
  }

  async checkSignIn() {
    if (await readlangClient.isLocalMode()) {
      signupModal.show({
        heading: "Sign in to chat",
        stores: stores,
      });
      return;
    }

    if (!stores.user.hasPremiumAccess.value) {
      premiumUpsellModal.show({
        title: "chat",
        heading: "Upgrade to chat with Ami...",
        showTitle: false,
      });
      return;
    }
  }

  upgrade = () => {
    premiumUpsellModal.show({
      title: "chatTab",
      heading: "Upgrade for conversations!",
      subheading: "Practice your written conversational skills and more!",
      showTitle: false,
      smartDefinitions: true,
    });
  };

  render() {
    if (this.state.type === "not-available") {
      return this.renderUpsell();
    }

    return (
      <div>
        {this.state.type === "loading" ? (
          <SkeletonView kind="rows" count={5} />
        ) : (
          <>
            {this.renderTopSection()}
            {this.state.userChats?.length > 0 ? (
              <>
                <h2>Previous conversations</h2>
                <div class="chatHistory">
                  <table>
                    {this.state.userChats.map((chat) => (
                      <tr>
                        <td>{new Date(chat.timestamp).toLocaleDateString()}</td>
                        <td title={chat.summary}>
                          <a href={`/chat/${chat._id}`}>{chat.title}</a>
                          <i
                            onClick={async () => {
                              if (
                                !confirm(`Are you sure you want to delete this chat?

This action can't be undone.`)
                              ) {
                                return;
                              }

                              // Delete the chat
                              await readlangClient.request(
                                "DELETE",
                                `/api/chat/${chat._id}`,
                                {}
                              );
                              this.fetchChatHistory();
                            }}
                            class="deleteButton icon icon-remove-sign"
                          ></i>
                        </td>
                      </tr>
                    ))}
                  </table>
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    );
  }

  renderTopSection() {
    if (this.state.type !== "loaded") {
      throw new Error("Unexpected state");
    }

    const languageString =
      languageMap.map[stores.user.currentLearningLanguage.value].english;

    if (this.state.recentNotificationMessage) {
      return (
        <>
          <h2>Suggested topic</h2>
          <p>
            <i>{this.state.recentNotificationMessage.message}</i>
            <i
              class="deleteButton icon icon-remove-sign"
              onClick={async () => {
                if (this.state.type !== "loaded") {
                  throw new Error("Unexpected state");
                }

                this.setState((prevState) => ({
                  ...prevState,
                  recentNotificationMessage: undefined,
                }));
                await readlangClient.request(
                  "DELETE",
                  `/api/chat/notification/${this.state.recentNotificationMessage._id}`,
                  {}
                );
              }}
            />
          </p>
          <button
            onClick={() => {
              if (this.state.type !== "loaded") {
                throw new Error("Unexpected state");
              }

              this.props.appRouter.navigate(
                `/chat/start?initialMessageID=${this.state.recentNotificationMessage._id}`,
                {
                  trigger: true,
                }
              );
            }}
            class="startButton prominent"
          >
            Start Chat
          </button>
        </>
      );
    }

    return (
      <>
        <h2>Start a new chat</h2>
        <p class="chatIntro">
          {this.state.userChats?.length > 0
            ? "Start a new chat with Ami"
            : "Make friends with Ami"}
          , a chatbot who will help you practice your conversational{" "}
          {languageString}.
        </p>
        <button
          onClick={() =>
            this.props.appRouter.navigate("/chat/start", {
              trigger: true,
            })
          }
          class="startButton prominent"
        >
          Start Chat
        </button>
      </>
    );
  }

  /** Without Premium: what conversations are, and an upgrade */
  renderUpsell() {
    const learningLanguage =
      languageMap.map[stores.user.currentLearningLanguage.value].english;
    const firstLanguage =
      languageMap.map[stores.user.firstLanguage.value].english;
    const benefits = [
      {
        icon: "icon-comments",
        title: "Never get stuck",
        text: `Write in ${learningLanguage}, and slip into ${firstLanguage} for any word you don't know yet.`,
      },
      {
        icon: "icon-ok",
        title: "Instant corrections",
        text: "Ami corrects every message you send, showing exactly what to change.",
      },
      {
        icon: "icon-th-large",
        title: "Remember what you learn",
        text: "Save your corrections as flashcards, to practice later.",
      },
    ];
    const quotes = [
      "This is just what I needed. It forces me to actively recall words to communicate effectively, and the best thing is I get immediate feedback and corrections.",
      "I tried this briefly today (for French) and value it more than standard chatbots, given the corrections.",
      "I've been dreaming of exactly this kind of interface since the beginning of the LLM trend. [...] It's probably my favorite feature in Readlang.",
    ];

    return (
      <div class="chatUpsell">
        <img
          class="chatUpsellRabbit"
          src="/content/landingPages/rabbit-chat.webp"
          alt=""
          width="720"
          height="660"
        />
        <p class="chatUpsellBadge">Premium</p>
        <h1>Chat with Ami</h1>
        <p class="chatUpsellIntro">
          Practice your {learningLanguage} in real conversations with Ami, an AI
          tutor who corrects you as you go.
        </p>
        <ul class="chatUpsellBenefits">
          {benefits.map(({ icon, title, text }) => (
            <li key={title}>
              <i class={icon} aria-hidden="true"></i>
              <div>
                <strong>{title}</strong>
                <p>{text}</p>
              </div>
            </li>
          ))}
        </ul>
        <Button kind="primary" size="large" block onClick={this.upgrade}>
          Upgrade to start chatting
        </Button>
        <h2>What learners say</h2>
        {quotes.map((quote) => (
          <blockquote key={quote}>{quote}</blockquote>
        ))}
      </div>
    );
  }
}

export default ChatListPageView;
